import {
  AlertTriangle,
  BarChart3,
  Package,
  ShoppingCart,
  Users,
} from "lucide-react";
import { Link, useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import PageHeader from "../components/PageHeader";
import Panel from "../components/Panel";
import StatCard from "../components/StatCard";
import StatusBadge from "../components/StatusBadge";
import { useToastOnServiceError } from "../hooks/useToastOnServiceError";
import { formatCompactNumber, formatDateTime, formatMoney } from "../utils/formatters";

function DashboardPage() {
  const navigate = useNavigate();
  const { products, categories, orders, customers, settings, source } = useSelector(
    (state) => state.admin
  );

  const lowStockProducts = products
    .filter((product) => product.stock <= settings.lowStockThreshold)
    .sort((left, right) => left.stock - right.stock)
    .slice(0, 5);
  const recentOrders = [...orders]
    .sort((left, right) => new Date(right.placedAt) - new Date(left.placedAt))
    .slice(0, 5);
  const pendingOrders = orders.filter(
    (order) =>
      order.fulfillmentStatus !== "Delivered" &&
      order.fulfillmentStatus !== "Cancelled"
  ).length;
  const paidRevenue = orders
    .filter((order) => order.paymentStatus === "Paid")
    .reduce((sum, order) => sum + order.total, 0);
  const categoryMix = categories
    .map((category) => ({
      ...category,
      productCount: products.filter((product) => product.categoryId === category.id).length,
    }))
    .sort((left, right) => right.productCount - left.productCount)
    .slice(0, 5);
  const orderPipeline = [
    "Pending",
    "Packed",
    "Out for Delivery",
    "Delivered",
    "Cancelled",
  ].map((status) => ({
    status,
    count: orders.filter((order) => order.fulfillmentStatus === status).length,
  }));
  const maxPipelineCount =
    Math.max(...orderPipeline.map((item) => item.count), 1) || 1;

  useToastOnServiceError(source.errorMessage, {
    title: "Workspace sync failed",
  });

  return (
    <div className="page-stack">
      <PageHeader
        eyebrow="Control room"
        title="Daily store pulse"
        description="Track revenue, inventory pressure, and live operational flow from one place."
        actions={
          <div className="cluster">
            <Link className="button button-secondary" to="/operations">
              Operations
            </Link>
            <Link className="button button-primary" to="/products/new">
              Add product
            </Link>
          </div>
        }
      />

      {source.mode !== "admin-api" ? (
        <div className="callout">
          {source.description}
        </div>
      ) : null}

      <div className="stat-grid">
        <StatCard
          icon={BarChart3}
          label="Paid revenue"
          value={formatMoney(paidRevenue)}
          hint="Captured across the current local order set"
          trend={{ label: "Healthy basket mix", direction: "up" }}
        />
        <StatCard
          icon={ShoppingCart}
          label="Orders in motion"
          value={formatCompactNumber(pendingOrders)}
          hint="Pending, packed, or out for delivery"
          tone="info"
        />
        <StatCard
          icon={Package}
          label="Active products"
          value={formatCompactNumber(products.filter((product) => product.inStock).length)}
          hint={`${products.length} catalog items across ${categories.length} categories`}
          tone="accent"
        />
        <StatCard
          icon={Users}
          label="Customer base"
          value={formatCompactNumber(customers.length)}
          hint="Seeded operational accounts for admin workflows"
          tone="success"
        />
      </div>

      <div className="dashboard-grid">
        <Panel
          title="Fulfillment pipeline"
          description="Current order movement through the daily delivery flow."
        >
          <div className="stack">
            {orderPipeline.map((item) => (
              <div key={item.status} className="pipeline-row">
                <div className="pipeline-copy">
                  <span>{item.status}</span>
                  <StatusBadge compact status={`${item.count} orders`} tone="neutral" />
                </div>
                <div className="progress-track">
                  <span
                    className="progress-fill"
                    style={{ width: `${(item.count / maxPipelineCount) * 100}%` }}
                  />
                </div>
              </div>
            ))}
          </div>
        </Panel>

        <Panel
          title="Category spread"
          description="Top product-heavy aisles based on the current workspace snapshot."
        >
          <div className="stack">
            {categoryMix.map((category) => (
              <div key={category.id} className="metric-row">
                <div>
                  <p className="metric-row-title">{category.name}</p>
                  <p className="metric-row-copy">
                    {category.productCount} listed products
                  </p>
                </div>
                <button
                  className="mini-link"
                  type="button"
                  onClick={() => navigate("/categories")}
                >
                  Manage
                </button>
              </div>
            ))}
          </div>
        </Panel>
      </div>

      <div className="dashboard-grid">
        <Panel
          title="Low stock queue"
          description={`Products at or below the ${settings.lowStockThreshold}-unit warning threshold.`}
          actions={<Link className="mini-link" to="/operations">Open operations</Link>}
        >
          <div className="table-shell">
            <table className="admin-table">
              <thead>
                <tr>
                  <th>Product</th>
                  <th>Category</th>
                  <th>Stock</th>
                  <th>Action</th>
                </tr>
              </thead>
              <tbody>
                {lowStockProducts.map((product) => (
                  <tr key={product.id}>
                    <td data-label="Product">
                      <div className="entity-cell">
                        <img alt={product.name} className="entity-avatar" src={product.image} />
                        <div>
                          <p className="entity-title">{product.name}</p>
                          <p className="entity-copy">{formatMoney(product.price)}</p>
                        </div>
                      </div>
                    </td>
                    <td data-label="Category">{product.categoryName}</td>
                    <td data-label="Stock">
                      <StatusBadge
                        status={
                          product.stock === 0 ? "Out of Stock" : `${product.stock} units left`
                        }
                        tone={product.stock === 0 ? "danger" : "warning"}
                      />
                    </td>
                    <td data-label="Action">
                      <button
                        className="mini-link"
                        type="button"
                        onClick={() => navigate(`/products/${product.id}`)}
                      >
                        Replenish
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Panel>

        <Panel
          title="Recent orders"
          description="Latest operational orders from the current workspace."
          actions={<Link className="mini-link" to="/orders">See all orders</Link>}
        >
          <div className="table-shell">
            <table className="admin-table">
              <thead>
                <tr>
                  <th>Order</th>
                  <th>Customer</th>
                  <th>Total</th>
                  <th>Status</th>
                </tr>
              </thead>
              <tbody>
                {recentOrders.map((order) => (
                  <tr key={order.id}>
                    <td data-label="Order">
                      <div>
                        <p className="entity-title">{order.orderNumber || order.id}</p>
                        <p className="entity-copy">{formatDateTime(order.placedAt || order.createdAt)}</p>
                      </div>
                    </td>
                    <td data-label="Customer">{order.customerName}</td>
                    <td data-label="Total">{formatMoney(order.total)}</td>
                    <td data-label="Status">
                      <div className="stack compact">
                        <StatusBadge status={order.fulfillmentStatus} />
                        <StatusBadge status={order.paymentStatus} tone="neutral" />
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Panel>
      </div>

      <Panel
        title="Workspace notes"
        description="Current admin mode and what it means for day-to-day operations."
      >
        <div className="note-grid">
          <div className="note-card">
            <div className="note-card-icon">
              <AlertTriangle size={18} />
            </div>
            <div>
              <p className="metric-row-title">Write model</p>
              <p className="metric-row-copy">
                Product, category, and brand writes now go through product-service.
                Orders, customers, and workspace settings remain local to this admin.
              </p>
            </div>
          </div>
          <div className="note-card">
            <div className="note-card-icon">
              <BarChart3 size={18} />
            </div>
            <div>
              <p className="metric-row-title">Read model</p>
              <p className="metric-row-copy">
                Product, category, and brand bootstrapping reads the product-service
                admin APIs first, then falls back to local seed data if unavailable.
              </p>
            </div>
          </div>
          <div className="note-card">
            <div className="note-card-icon">
              <Package size={18} />
            </div>
            <div>
              <p className="metric-row-title">Threshold tracking</p>
              <p className="metric-row-copy">
                Inventory warnings are controlled by the low stock threshold in Settings.
              </p>
            </div>
          </div>
        </div>
      </Panel>
    </div>
  );
}

export default DashboardPage;
